import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { ApiError, api, onUnauthorized } from "../api/client";
import { qk } from "../api/queries";
import type { User } from "../api/types";
import { PageLoading, ErrorBox } from "../components/Spinner";

async function fetchMe(): Promise<User | null> {
  try {
    return await api.me();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: fetchMe, staleTime: 5 * 60_000 });
}

/** Current user; only valid inside <RequireAuth>. */
export function useCurrentUser(): User {
  const { data } = useMe();
  if (!data) throw new Error("useCurrentUser outside RequireAuth");
  return data;
}

/** Any 401 from the API clears the session, which redirects to /login. */
export function UnauthorizedWatcher() {
  const qc = useQueryClient();
  useEffect(
    () =>
      onUnauthorized(() => {
        qc.setQueryData(qk.me, null);
      }),
    [qc],
  );
  return null;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useMe();
  const loc = useLocation();
  if (isLoading) return <PageLoading />;
  if (error) return <ErrorBox error={error} />;
  if (!data) {
    const next = loc.pathname + loc.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}
